// eslint-disable-next-line import/no-extraneous-dependencies
import * as amplitude from '@amplitude/analytics-browser';
import { Input } from '@components';
import { EVENTS } from '@constants';
import { yupResolver } from '@hookform/resolvers/yup';
import { useScreenSize } from '@hooks';
import SignIn from '@pngs/AuthForm/SignIn.png';
import { authApi, authSlice } from '@store';
import { theme, Typography } from '@styles';
import { AuthModalTemplate } from '@templates';
import { schemas } from '@utils';
import { func } from 'prop-types';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { getErrorNotificationText } from 'src/helpers/error';

const SignInForm = ({ onSignInButtonClick }) => {
  const { isTabletSM } = useScreenSize();

  const [signIn, { isLoading: isSignInMutationLoading }] =
    authApi.useSignInMutation();

  const { clearErrors, control, formState, handleSubmit, reset, setError } =
    useForm({
      defaultValues: {
        password: '',
        username: '',
      },
      mode: 'onChange',
      reValidateMode: 'onChange',
      resolver: yupResolver(schemas.signInForm),
    });

  const { i18n } = useTranslation();
  const dispatch = useDispatch();

  // const handleForgotPasswordButtonClick = () => {
  //   dispatch(authSlice.actions.setAuthModalState('emailForm'));
  // };

  const handleSignUpButtonClick = () => {
    dispatch(authSlice.actions.setAuthModalState('signUp'));
  };

  const errorNotificationText = getErrorNotificationText(formState.errors);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const isErrorNotificationVisible = !!errorNotificationText;

  const handleLogInButtonClick = () => {
    setIsErrorVisible(true);
    handleSubmit(async (formData) => {
      try {
        clearErrors('server');

        const { data: signInResponse } = await signIn({
          password: formData.password,
          username: formData.username,
        }).unwrap();
        amplitude.track(EVENTS.signedIn, { after_registration: false });
        amplitude.setUserId(`user${signInResponse.id}`);
        dispatch(authSlice.actions.setTokens(signInResponse));
        dispatch(authSlice.actions.setAvatar(signInResponse.avatar));
        dispatch(authSlice.actions.setUserId(signInResponse.id));

        reset();

        onSignInButtonClick();
      } catch ({ data }) {
        setError('server', {
          message: data.error.message,
          type: 'server',
        });
      }
    })();
  };

  return (
    <FormProvider control={control} formState={formState}>
      <AuthModalTemplate
        backgroundImage={SignIn}
        buttonDisabled={
          isErrorVisible && (!formState.isValid || isSignInMutationLoading)
        }
        buttonText={i18n.t('AuthForm.LogIn').toUpperCase()}
        formComponent={
          <>
            <Input mb={3} name="username" type="text" variant="username" />
            <Input
              isErrorVisible={isErrorVisible}
              mb={isErrorNotificationVisible && isErrorVisible ? 1 : 3}
              name="password"
              setIsErrorVisible={setIsErrorVisible}
              type="password"
              variant="password"
            />
            {isErrorNotificationVisible && isErrorVisible && (
              <Typography
                color={theme.colors.red[100]}
                mb={3}
                variant="MS-regular-s16-lh25"
              >
                {!formState.errors.server
                  ? i18n.t(errorNotificationText)
                  : errorNotificationText}
              </Typography>
            )}
          </>
        }
        onMainButtonClick={handleLogInButtonClick}
        // onSubButtonClick={handleForgotPasswordButtonClick}
        onSubtitleButtonClick={handleSignUpButtonClick}
        rightContainerProps={isTabletSM ? { px: 16 } : { pl: 40, pr: 64 }}
        // subButtonText={i18next.t('AuthForm.ForgotPassword')}
        subtitleText={i18n.t('AuthForm.SignUp')}
        titleText={i18n.t('AuthForm.CreateNewAccount')}
      />
    </FormProvider>
  );
};

SignInForm.propTypes = {
  onSignInButtonClick: func.isRequired,
};

export default SignInForm;
